/**
 * voice-v8 — die Frage der App an den Server: „Ist die Mitschrift ein fertiger
 * Satz?" (POST /api/stille-auto, server/stille-auto-routen.ts)
 *
 * „Absenden nach Stille: Auto" (Ticket 2763): Wahrscheinlichkeit 0…1 — oder
 * `null` bei jedem Ausfall, auch nach AUTO_ANTWORT_FRIST_MS ohne Antwort. Dann
 * gilt die feste Frist (shared/stille-auto.ts); nichts wartet hier länger als
 * diese Frist.
 *
 * Ticket 2867 (01.10.2026): aus app.ts herausgelöst, damit der Vertrag mit dem
 * Server prüfbar ist. Die App schickt die Stille bei der Frage mit (der Server
 * schreibt ihre Stufe ins Journal); die Antwort sagt, ob die gestufte Schwelle
 * gilt (`stufen`, Schalter `STILLE_STUFEN_AUS` am Server).
 *
 * 02.10.2026: ebenso `satzzeichen: true` für die Satzzeichen-Regel (Schalter
 * `STILLE_SATZZEICHEN` am Server).
 *
 * 03.10.2026 (Fantasiewörter): `rausch` — die Messung der Endwörter
 * (shared/rausch-woerter.ts) geht mit; die Antwort sagt mit `rauschFilter:
 * true`, ob der Server die Fantasiewörter fürs Urteil wegließ.
 *
 * 05.10.2026: ebenso `textruhe: true` für „Laut ohne neues Wort“ (Schalter
 * `STILLE_TEXTRUHE` am Server, shared/stille-auto.ts `autoJevStille`).
 *
 * 07.10.2026: ebenso `sprachgrenze: true` für die Sprachgrenze der Anzeige (Schalter
 * `STILLE_SPRACHGRENZE` am Server, frontend/lib/mic-visualizer.ts `SPRACH_ABSTAND`).
 *
 * 07.10.2026: ebenso `komma: true` für „Komma am Ende“ (Schalter `STILLE_KOMMA` am
 * Server, shared/stille-auto.ts `kommaAmEnde`). Mit der Frage geht `folge` mit — die
 * wievielte Frage in dieser Pause und ob sich nur das Satzzeichen am Ende änderte;
 * nur für die Zeile `[stille-auto]` (`frage=`, `erneut=`), es entscheidet nichts.
 *
 * 08.10.2026: ebenso `mittel: true` für das „mittlere Urteil“ (Schalter `STILLE_MITTEL`,
 * shared/stille-auto.ts `AUTO_MITTEL_HOECHSTENS_MS`). Die Anfrage selbst bleibt unverändert.
 *
 * 12.10.2026: `fein` (Schalter `STILLE_FEIN`, Standard an: bei geschlossenem Satz früher fragen, Antwort sofort
 * auswerten) kommt als `true` UND als `false` durch — die Karte merkt sich die letzte Antwort des Servers; ein
 * Ausfall ändert nichts daran. `deckelKurz: true` (Schalter `STILLE_DECKEL_KURZ`, Standard aus: Obergrenze 5,0 s bei
 * geschlossenem Satz und Jev < 0,5) schaltet nur das ausdrückliche `true` ein.
 *
 * 12.10.2026 (Bahn „fortsetzung-anhaengen“): `fortsetzung` (Schalter `FORTSETZUNG`, Standard an; `true` und `false` kommen durch) und `tempo` (`STILLE_TEMPO`, nur
 * „flott“/„blitz“ kommen durch, alles andere ist `ruhig` und wird gar nicht erst eingetragen) — getrennt von `fein` und `deckelKurz`. Die
 * Anfrage selbst bleibt unverändert. Die Karte nimmt `tempo` nur zusammen mit `fortsetzung: true` (ptt-modal.ts).
 *
 * 15.10.2026 (Bahn „stille-zusage“): `zusage` (Schalter `STILLE_ZUSAGE`, Standard an; `true` und `false` kommen durch) — eine kurze
 * Zusage geht nach 1,2 s hinaus (shared/stille-auto.ts `kurzeZusage`). Die Anfrage selbst bleibt unverändert.
 * 15.10.2026 (Bahn „wortstille“): ebenso `wortruhe` (Schalter `STILLE_WORTRUHE`, Standard an; shared/stille-auto.ts `autoWortStille`).
 * 16.10.2026 (Bahn „pause-kuerzen“): `frageKurz: true` (Schalter `STILLE_FRAGE_KURZ`, Standard an: Obergrenze 4,0 s nach „?“ bei
 * Jev 0,3–0,49, shared/stille-auto.ts `AUTO_FRAGE_HOECHSTENS_MS`) — nur das ausdrückliche `true` schaltet ein, wie `deckelKurz`.
 * 16.10.2026 (Bahn „jev-offenes-ende“): ebenso `offenStreng: true` (Schalter `STILLE_OFFEN_STRENG`, Standard an: ohne Satzzeichen
 * am Ende sendet Jev vor 2,5 s nur ab 0,92, nach Füllwort/Komma gar nicht — shared/stille-auto.ts `offenesEnde`).
 * 16.10.2026 (Bahn „obergrenze-kurz“): ebenso `obergrenzeKurz: true` (Schalter `STILLE_OBERGRENZE_KURZ`, Standard an: Obergrenze
 * 4 s, nach Füllwort/Komma/Doppelpunkt/Strich 6 s — shared/stille-auto.ts `obergrenzeKurzMs`).
 * 17.10.2026 (Bahn „wortstille-unfertig“): ebenso `wortruheUnfertig: true` (Schalter `STILLE_WORTRUHE_UNFERTIG`, Standard an:
 * Aufschub der Wortstille bei klar unfertigem Satz unter Ton — shared/stille-auto.ts `wortRuheZaehlt`).
 *
 * ⚠️ NUR ein ausdrückliches `stufen: true` schaltet die Stufen ein. Fehlt die
 *    Angabe oder scheitert die Frage, gilt die feste Schwelle — im Zweifel das
 *    alte Verhalten.
 */
import { AUTO_ANTWORT_FRIST_MS, tempoLesen, type AutoAntwort, type AutoFolge } from "../../shared/stille-auto.js";
import type { RauschMessung } from "../../shared/rausch-woerter.js";
import { aufnahmeKennung } from "./aufnahme-kennung.js";

export async function frageStilleAuto(
  text: string,
  stillMs?: number,
  abruf: typeof fetch = (...a) => fetch(...a),
  rausch?: RauschMessung,
  folge?: AutoFolge,
): Promise<AutoAntwort> {
  const stopp = new AbortController();
  const wecker = window.setTimeout(() => stopp.abort(), AUTO_ANTWORT_FRIST_MS);
  try {
    const r = await abruf("/api/stille-auto", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      // Welle 0: Kennung der Aufnahme (nur wenn eine läuft) — nur für die Journalzeile des Servers.
      body: JSON.stringify({ text, stillMs, ...(rausch ? { rausch } : {}), ...(folge ? { frage: folge.frage, erneut: folge.erneut } : {}), ...(aufnahmeKennung() ? { az: aufnahmeKennung() } : {}) }),
      signal: stopp.signal,
    });
    if (!r.ok) return { fertig: null, stufen: false, satzzeichen: false };
    const d = (await r.json()) as {
      fertig?: unknown;
      stufen?: unknown;
      satzzeichen?: unknown;
      rauschFilter?: unknown;
      textruhe?: unknown;
      sprachgrenze?: unknown;
      komma?: unknown;
      mittel?: unknown;
      fein?: unknown;
      deckelKurz?: unknown;
      frageKurz?: unknown;
      offenStreng?: unknown;
      obergrenzeKurz?: unknown;
      fortsetzung?: unknown;
      tempo?: unknown;
      zusage?: unknown;
      wortruhe?: unknown;
      wortruheUnfertig?: unknown;
    };
    return {
      fertig: typeof d.fertig === "number" && Number.isFinite(d.fertig) ? d.fertig : null,
      stufen: d.stufen === true,
      satzzeichen: d.satzzeichen === true,
      ...(d.rauschFilter === true ? { rauschFilter: true } : {}),
      // 05.10.2026: „Laut ohne neues Wort“ nur mit ausdrücklichem Ja des Servers.
      ...(d.textruhe === true ? { textruhe: true } : {}),
      // 07.10.2026: ebenso die Sprachgrenze der Anzeige …
      ...(d.sprachgrenze === true ? { sprachgrenze: true } : {}),
      // … und „Komma am Ende“.
      ...(d.komma === true ? { komma: true } : {}),
      // 08.10.2026: … und das „mittlere Urteil“.
      ...(d.mittel === true ? { mittel: true } : {}),
      // 12.10.2026: … und der Feinschliff.
      ...(typeof d.fein === "boolean" ? { fein: d.fein } : {}),
      // … und der kurze Deckel (nur ein ausdrückliches Ja).
      ...(d.deckelKurz === true ? { deckelKurz: true } : {}),
      // 16.10.2026: … und die kürzere Obergrenze nach „?“ bei Jev 0,3–0,49 (nur ein ausdrückliches Ja).
      ...(d.frageKurz === true ? { frageKurz: true } : {}),
      // 16.10.2026: … und das offene Ende (nur ein ausdrückliches Ja).
      ...(d.offenStreng === true ? { offenStreng: true } : {}),
      // 16.10.2026: … und die kurze Obergrenze 4 s / 6 s (nur ein ausdrückliches Ja).
      ...(d.obergrenzeKurz === true ? { obergrenzeKurz: true } : {}),
      // 12.10.2026: … die Fortsetzung (wie `fein`: `true` UND `false` kommen durch — ein Ausfall ändert nichts am Stand der Seite) und die Tempo-Stufe (nur „flott“/„blitz“).
      ...(typeof d.fortsetzung === "boolean" ? { fortsetzung: d.fortsetzung } : {}),
      ...(tempoLesen(d.tempo) !== "ruhig" ? { tempo: tempoLesen(d.tempo) } : {}),
      // 15.10.2026: … und „kurze Zusage“ (wie `fein`: `true` und `false` kommen durch, die Karte merkt sich die letzte Antwort).
      ...(typeof d.zusage === "boolean" ? { zusage: d.zusage } : {}),
      // 15.10.2026: … und „Wortstille“ (wie `zusage`: `true` und `false` kommen durch, die Karte merkt sich die letzte Antwort).
      ...(typeof d.wortruhe === "boolean" ? { wortruhe: d.wortruhe } : {}),
      // 17.10.2026: … und der Aufschub bei klar unfertigem Satz (nur ein ausdrückliches Ja).
      ...(d.wortruheUnfertig === true ? { wortruheUnfertig: true } : {}),
    };
  } catch {
    return { fertig: null, stufen: false, satzzeichen: false };
  } finally {
    window.clearTimeout(wecker);
  }
}
